import { useRef, useState } from 'react';
import { Settings2 } from 'lucide-react';
import { Popover } from '@/components/ui/popover';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { TextSize, TextWeight } from '@/api/reviews';

/** Стиль одного текста: размер, вес и автокапс. Один тип на форму и на подпись
 *  под QR-кодом — шестерёнка у них общая (Алексей 18.09, AI63). */
export interface TextStyle {
  size: TextSize;
  weight: TextWeight;
  /** Автокапс. undefined = включён (так было до появления выключателя). */
  caps?: boolean;
}

const SIZES: { key: TextSize; label: string }[] = [
  { key: 'sm', label: 'S' }, { key: 'md', label: 'M' }, { key: 'lg', label: 'L' }, { key: 'xl', label: 'XL' },
];

/** Размер и вес текста — под шестерёнкой в конце поля: быстрый тултип по
 *  наведению, по клику небольшое меню, не модалка (Алексей 16.09, п. 10). */
export function TextStyleGear({ value, onChange }: {
  value: TextStyle;
  onChange: (p: Partial<TextStyle>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <>
      <Tooltip content="Размер и вес текста" openDelay={0} disabled={open}>
        <button
          ref={ref}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label="Размер и вес текста"
          className={cn('inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted hover:text-foreground', open && 'bg-muted text-foreground')}
        >
          <Settings2 className="size-4" />
        </button>
      </Tooltip>
      <Popover open={open} onOpenChange={setOpen} triggerRef={ref} align="end" className="w-52 rounded-lg border border-border bg-card p-2 shadow-md">
        <div className="mb-1 px-1 text-[10px] uppercase tracking-wider text-muted-foreground">Размер</div>
        <div className="flex overflow-hidden rounded-md border border-border">
          {SIZES.map((sz) => (
            <button
              key={sz.key}
              type="button"
              onClick={() => onChange({ size: sz.key })}
              className={cn('h-8 flex-1 text-xs', value.size === sz.key ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}
            >
              {sz.label}
            </button>
          ))}
        </div>
        <div className="mb-1 mt-2 px-1 text-[10px] uppercase tracking-wider text-muted-foreground">Вес</div>
        <div className="flex overflow-hidden rounded-md border border-border">
          {([['normal', 'Обычный'], ['bold', 'Жирный']] as const).map(([w, label]) => (
            <button
              key={w}
              type="button"
              onClick={() => onChange({ weight: w })}
              className={cn('h-8 flex-1 text-xs', w === 'bold' && 'font-semibold', value.weight === w ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}
            >
              {label}
            </button>
          ))}
        </div>
        {/* Автокапс — выключатель (Алексей 18.09, AI63). Оформление Surf рисует
            заголовок капслоком; кому-то это не нужно, и выключать это должно быть
            можно везде, где есть эта шестерёнка. */}
        <div className="mb-1 mt-2 px-1 text-[10px] uppercase tracking-wider text-muted-foreground">Автокапс</div>
        <div className="flex overflow-hidden rounded-md border border-border">
          {([[true, 'ВКЛ'], [false, 'выкл']] as const).map(([v, label]) => (
            <button
              key={String(v)}
              type="button"
              onClick={() => onChange({ caps: v })}
              className={cn('h-8 flex-1 text-xs', (value.caps ?? true) === v ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}
            >
              {label}
            </button>
          ))}
        </div>
      </Popover>
    </>
  );
}
